import {useCallback, useEffect, useRef, useState} from "react";
import {instance} from "../../../../../../API/API";
import {message} from "antd";

const VAT_RATE = 0.13;

export const useStaffData = (visible, isEditMode = false, existingStaffDetails = []) => {
    const [staffLoading, setStaffLoading] = useState(false);
    const [availableStaff, setAvailableStaff] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState([]);
    const [staffAmounts, setStaffAmounts] = useState({});

    const isInitializedRef = useRef(false);

    const fetchActiveStaff = useCallback(async () => {
        try {
            setStaffLoading(true);
            const response = await instance.get(`finance_04122025/get_active_staff.php`, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success && Array.isArray(response.data.staff)) {
                const allStaff = response.data.staff.map(staff => ({
                    ...staff,
                    key: staff.id.toString()
                }));

                // Фильтруем уже выбранных сотрудников
                const selectedIds = selectedStaff.map(s => s.id);
                const filteredStaff = allStaff.filter(staff => !selectedIds.includes(staff.id));

                setAvailableStaff(filteredStaff);
            } else {
                message.error('Не удалось загрузить список сотрудников');
            }
        } catch (error) {
            console.error('Ошибка загрузки сотрудников:', error);
            message.error('Ошибка загрузки списка сотрудников');
        } finally {
            setStaffLoading(false);
        }
    }, [selectedStaff]);

    // Инициализация выбранных сотрудников для режима редактирования
    useEffect(() => {
        if (isEditMode && existingStaffDetails.length > 0 && !isInitializedRef.current) {
            console.log('Инициализация useStaffData с сотрудниками:', existingStaffDetails);

            const selected = existingStaffDetails.map(staff => {
                const fullName = staff.fullName ||
                    `${staff.lastName || staff.lastname || ''} ${staff.firstName || staff.firstname || ''}`.trim() ||
                    `Сотрудник #${staff.id}`;

                return {
                    id: staff.id,
                    fullName: fullName,
                    firstName: staff.firstName || staff.firstname || '',
                    lastName: staff.lastName || staff.lastname || '',
                    storeId: staff.storeId || staff.store_id || null,
                    key: staff.id.toString()
                };
            });

            setSelectedStaff(selected);

            const amounts = {};
            existingStaffDetails.forEach(staff => {
                amounts[staff.id] = staff.amounts || {
                    cash: 0,
                    nonCashWithoutVAT: 0,
                    nonCashWithVAT: 0
                };
            });
            setStaffAmounts(amounts);

            isInitializedRef.current = true;
        }
    }, [isEditMode, existingStaffDetails]);

    // Загружаем активных сотрудников при открытии модального окна
    useEffect(() => {
        if (visible && !isInitializedRef.current) {
            fetchActiveStaff();
        }

        return () => {
            if (!visible) {
                isInitializedRef.current = false;
            }
        };
    }, [visible, fetchActiveStaff]);

    // Обновляем доступных сотрудников при изменении выбранных
    useEffect(() => {
        if (isInitializedRef.current && selectedStaff.length > 0 && availableStaff.length > 0) {
            const currentAvailable = [...availableStaff];
            const selectedIds = selectedStaff.map(s => s.id);
            const filteredStaff = currentAvailable.filter(staff => !selectedIds.includes(staff.id));

            if (JSON.stringify(filteredStaff) !== JSON.stringify(currentAvailable)) {
                setAvailableStaff(filteredStaff);
            }
        }
    }, [selectedStaff, availableStaff]);

    const handleAddStaff = useCallback((staffId) => {
        const staff = availableStaff.find(s => s.id === staffId);
        if (staff && !selectedStaff.some(s => s.id === staffId)) {
            setSelectedStaff(prev => [...prev, staff]);
            setAvailableStaff(prev => prev.filter(s => s.id !== staffId));

            setStaffAmounts(prev => ({
                ...prev,
                [staff.id]: {
                    cash: 0,
                    nonCashWithoutVAT: 0,
                    nonCashWithVAT: 0
                }
            }));
        }
    }, [availableStaff, selectedStaff]);

    const handleRemoveStaff = useCallback((staffId) => {
        const staff = selectedStaff.find(s => s.id === staffId);
        if (staff) {
            setSelectedStaff(prev => prev.filter(s => s.id !== staffId));

            setAvailableStaff(prev => {
                const newAvailable = [...prev, staff];
                return newAvailable.sort((a, b) => a.fullName.localeCompare(b.fullName));
            });

            setStaffAmounts(prev => {
                const newAmounts = {...prev};
                delete newAmounts[staffId];
                return newAmounts;
            });
        }
    }, [selectedStaff]);

    const handleAddAllStaff = useCallback(() => {
        const staffToAdd = [...availableStaff];
        const newAmounts = {...staffAmounts};

        staffToAdd.forEach(staff => {
            if (!selectedStaff.some(s => s.id === staff.id)) {
                newAmounts[staff.id] = {
                    cash: 0,
                    nonCashWithoutVAT: 0,
                    nonCashWithVAT: 0
                };
            }
        });

        setSelectedStaff(prev => [...prev, ...staffToAdd]);
        setAvailableStaff([]);
        setStaffAmounts(newAmounts);
    }, [availableStaff, staffAmounts, selectedStaff]);

    const handleAmountChange = useCallback((staffId, field, value) => {
        const amount = parseFloat(value) || 0;

        setStaffAmounts(prev => {
            const currentAmounts = prev[staffId] || {cash: 0, nonCashWithoutVAT: 0, nonCashWithVAT: 0};
            const newAmounts = {...currentAmounts};

            if (field === 'nonCashWithoutVAT') {
                newAmounts.nonCashWithoutVAT = amount;
                newAmounts.nonCashWithVAT = Math.round(amount * (1 + VAT_RATE) * 100) / 100;
            } else if (field === 'nonCashWithVAT') {
                newAmounts.nonCashWithVAT = amount;
                newAmounts.nonCashWithoutVAT = Math.round(amount / (1 + VAT_RATE) * 100) / 100;
            } else {
                newAmounts[field] = amount;
            }

            return {
                ...prev,
                [staffId]: newAmounts
            };
        });
    }, []);

    const resetStaffData = useCallback(() => {
        setSelectedStaff([]);
        setStaffAmounts({});
        setAvailableStaff([]);
        isInitializedRef.current = false;
    }, []);

    return {
        staffLoading,
        availableStaff,
        selectedStaff,
        staffAmounts,
        handleAddStaff,
        handleRemoveStaff,
        handleAddAllStaff,
        handleAmountChange,
        fetchActiveStaff,
        resetStaffData,
        setSelectedStaff,
        setStaffAmounts
    };
};